import { ReactNode, createContext, useState, useEffect } from "react";
import {
  useGetModelBundleQuery,
  BundlePlatformEnum,
  ModelBundle,
  BundlePlatformFilesInput,
} from "../../../generated/graphql";
import { LoadingProgressOverlay } from "../../shared/LoadingProgress";

const initialErrorsState: Record<string, boolean> = {
  name: false,
  number: false,
  contentType: false,
  isDraft: false,
};

export interface ModelBundleFormData extends Omit<ModelBundle, "platforms"> {
  platforms: BundlePlatformFilesInput[];
}

export interface IBundleFormModalContext {
  modelData: ModelBundleFormData;
  errors: Record<string, boolean>;
  activePlatform: BundlePlatformEnum | null;
  handleError: (field: string, value?: boolean) => void;
  updateModelData: <Key extends keyof ModelBundleFormData>(
    keyOrData: Key | Partial<ModelBundleFormData>,
    value?: ModelBundleFormData[Key],
  ) => void;
  handleActivePlatform: (platform: BundlePlatformEnum) => void;
}

export const BundleFormModalContext = createContext<IBundleFormModalContext>({
  modelData: {} as ModelBundleFormData,
  errors: initialErrorsState,
  activePlatform: null,
  handleError: () => {},
  updateModelData: () => {},
  handleActivePlatform: () => {},
});

export const BundleFormModalProvider = ({
  children,
  id,
}: {
  children: ReactNode;
  id?: string;
}) => {
  const [modelData, setModelData] = useState<ModelBundleFormData>(
    {} as ModelBundleFormData,
  );
  const [activePlatform, setActivePlatform] =
    useState<BundlePlatformEnum | null>(null);
  const [errors, setErrors] =
    useState<Record<string, boolean>>(initialErrorsState);

  const { data, loading } = useGetModelBundleQuery({
    variables: { getModelBundleId: id },
    skip: !id,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (data?.getModelBundle?.id) {
      setModelData({ ...data.getModelBundle, platforms: [] });
    }
  }, [data]);

  if (loading) {
    return <LoadingProgressOverlay />;
  }

  const updateModelData = <Key extends keyof ModelBundleFormData>(
    keyOrData: Key | Partial<ModelBundleFormData>,
    value?: ModelBundleFormData[Key],
  ) => {
    const update =
      typeof keyOrData === "object" ? keyOrData : { [keyOrData]: value };

    setModelData((prev) => ({
      ...prev,
      ...update,
    }));
  };

  const handleError = (field: string, value: boolean = true) => {
    setErrors((prev) => ({ ...prev, [field]: value }));
  };

  const handleActivePlatform = (platform: BundlePlatformEnum) => {
    setActivePlatform(platform);
  };

  return (
    <BundleFormModalContext.Provider
      value={{
        modelData,
        errors,
        activePlatform,
        updateModelData,
        handleError,
        handleActivePlatform,
      }}
    >
      {children}
    </BundleFormModalContext.Provider>
  );
};
